/**
 * Smart Prospect Question System - Response Processor
 * 
 * Parses LLM responses for [PROSPECT]: tags and replaces them with
 * curated questions from the question manager system.
 */

import { prospectQuestionManager } from './prospectQuestionManager';

interface ProspectMatch {
  fullMatch: string;          // "[PROSPECT]: Original question text"
  originalQuestion: string;   // "Original question text"
  startIndex: number;         // Position in response
  endIndex: number;           // End position in response
  tagFormat: string;          // Which tag format was matched
}

interface ProcessingResult {
  success: boolean;
  originalResponse: string;
  enhancedResponse: string;
  replacementsMade: number;
  processingTimeMs: number;
  errors?: string[];
}

interface ProcessorConfig {
  enableProcessing: boolean;
  maxProcessingTimeMs: number;
  fallbackOnError: boolean;
  logReplacements: boolean;
  maxReplacements: number; // Prevent infinite processing
}

class ResponseProcessor {
  private static instance: ResponseProcessor;
  private config: ProcessorConfig;
  
  // Regex patterns for different [PROSPECT] tag formats
  private readonly patterns = {
    // Primary pattern - exact format: [PROSPECT]: Question text
    primary: /\[PROSPECT\]:\s*(.+?)(?=\n\n|\n$|$)/gi,
    
    // Fallback patterns for variations
    fallbacks: [
      /\[PROSPECT\]\s*:?\s*(.+?)(?=\n\n|\n$|$)/gi,    // Missing colon or extra spaces
      /PROSPECT:\s*(.+?)(?=\n\n|\n$|$)/gi,            // Missing brackets
      /\[PROSPECT:\s*(.+?)(?=\]|\n\n|\n$|$)/gi,       // Missing closing bracket or colon
      /\[PROSPECT\s*\]:\s*(.+?)(?=\n\n|\n$|$)/gi      // Space before closing bracket
    ]
  };

  private constructor() {
    this.config = {
      enableProcessing: true,
      maxProcessingTimeMs: 50,  // Performance timeout
      fallbackOnError: true,
      logReplacements: process.env.NODE_ENV === 'development',
      maxReplacements: 5        // Prevent runaway processing
    };
  }

  static getInstance(): ResponseProcessor {
    if (!ResponseProcessor.instance) {
      ResponseProcessor.instance = new ResponseProcessor();
    }
    return ResponseProcessor.instance;
  }

  /**
   * Main function: Enhance response by replacing [PROSPECT]: tags with curated questions
   */
  enhanceProspectQuestions(response: string): string {
    if (!this.config.enableProcessing) {
      return response;
    }

    const startTime = performance.now();
    
    try {
      // Validate input
      if (!response || typeof response !== 'string') {
        this.logError('Invalid response input', { response: typeof response });
        return response;
      }

      // Find all prospect tag matches
      const matches = this.findProspectTags(response);
      
      if (matches.length === 0) {
        // No [PROSPECT] tags found - return original
        return response;
      }

      // Check processing timeout
      if (performance.now() - startTime > this.config.maxProcessingTimeMs) {
        this.logError('Processing timeout during parsing', { 
          timeElapsed: performance.now() - startTime,
          timeout: this.config.maxProcessingTimeMs 
        });
        return this.config.fallbackOnError ? response : response;
      }

      // Limit number of replacements
      const limitedMatches = matches.slice(0, this.config.maxReplacements);
      if (limitedMatches.length < matches.length) {
        this.logError('Too many prospect tags, limiting replacements', {
          found: matches.length,
          processing: limitedMatches.length
        });
      }

      // Replace each match with a curated question
      let enhancedResponse = response;
      let replacementsMade = 0;

      // Process matches in reverse order to maintain string indices
      const reversedMatches = [...limitedMatches].reverse();
      
      for (const match of reversedMatches) {
        const curatedQuestion = prospectQuestionManager.selectQuestion();
        
        if (curatedQuestion) {
          const replacement = `[PROSPECT]: ${curatedQuestion.text}`;
          enhancedResponse = enhancedResponse.substring(0, match.startIndex) + 
                           replacement + 
                           enhancedResponse.substring(match.endIndex);
          replacementsMade++;
          
          this.logReplacement(match, curatedQuestion.text, curatedQuestion.id);
        } else {
          // No curated question available, keep original
          this.logError('No curated question available', { originalQuestion: match.originalQuestion });
        }
        
        // Check timeout again during processing
        if (performance.now() - startTime > this.config.maxProcessingTimeMs) {
          this.logError('Processing timeout during replacement', { 
            timeElapsed: performance.now() - startTime,
            replacementsMade 
          });
          break;
        }
      }

      // Validate the enhanced response
      if (!this.validateResponse(response, enhancedResponse)) {
        this.logError('Response validation failed, returning original');
        return this.config.fallbackOnError ? response : response;
      }

      const processingTime = performance.now() - startTime;
      
      if (this.config.logReplacements) {
        console.log(`[ResponseProcessor] Enhanced response: ${replacementsMade} replacements in ${Math.round(processingTime * 100) / 100}ms`);
      }

      return enhancedResponse;

    } catch (error) {
      // Log error but always return a valid response
      const err = error as Error;
      this.logError('Processing failed completely', { 
        error: err.message,
        stack: err.stack,
        responseLength: response?.length,
        responsePreview: response?.substring(0, 100)
      });
      
      return this.config.fallbackOnError ? response : response;
    }
  }

  /**
   * Find all [PROSPECT]: patterns in response text
   */
  findProspectTags(response: string): ProspectMatch[] {
    const matches: ProspectMatch[] = [];

    try {
      // Try primary pattern first
      const primaryMatches = this.findMatchesWithPattern(response, this.patterns.primary, 'primary');
      matches.push(...primaryMatches);

      // If no primary matches, try fallback patterns
      if (matches.length === 0) {
        for (let i = 0; i < this.patterns.fallbacks.length; i++) {
          const fallbackMatches = this.findMatchesWithPattern(
            response, 
            this.patterns.fallbacks[i], 
            `fallback-${i + 1}`
          );
          matches.push(...fallbackMatches);
          
          if (matches.length > 0) {
            break; // Use first successful fallback pattern
          }
        }
      }

      // Remove duplicates (same start position)
      const uniqueMatches = matches.filter((match, index, array) => 
        index === 0 || match.startIndex !== array[index - 1].startIndex
      );

      // Sort by position (descending for safe replacement)
      return uniqueMatches.sort((a, b) => b.startIndex - a.startIndex);

    } catch (error) {
      this.logError('Failed to find prospect tags', error);
      return [];
    }
  }

  /**
   * Find matches using a specific regex pattern
   */
  private findMatchesWithPattern(response: string, pattern: RegExp, patternName: string): ProspectMatch[] {
    const matches: ProspectMatch[] = [];
    let match: RegExpExecArray | null;

    // Reset regex lastIndex to ensure fresh matching
    pattern.lastIndex = 0;

    while ((match = pattern.exec(response)) !== null) {
      if (matches.length >= this.config.maxReplacements) {
        break; // Prevent infinite loops
      }

      const fullMatch = match[0];
      const originalQuestion = match[1]?.trim() || '';
      
      if (originalQuestion.length > 0) {
        matches.push({
          fullMatch,
          originalQuestion,
          startIndex: match.index,
          endIndex: match.index + fullMatch.length,
          tagFormat: patternName
        });
      }

      // Prevent infinite loops with zero-width matches
      if (match.index === pattern.lastIndex) {
        pattern.lastIndex++;
      }
    }

    return matches;
  }

  /**
   * Validate that the enhanced response is reasonable
   */
  validateResponse(originalResponse: string, enhancedResponse: string): boolean {
    try {
      // Basic sanity checks
      if (!enhancedResponse || typeof enhancedResponse !== 'string') {
        return false;
      }

      // Response shouldn't be dramatically different in length
      const lengthRatio = enhancedResponse.length / originalResponse.length;
      if (lengthRatio < 0.5 || lengthRatio > 3.0) {
        this.logError('Response length change too dramatic', {
          originalLength: originalResponse.length,
          enhancedLength: enhancedResponse.length,
          ratio: lengthRatio
        });
        return false;
      }

      // Enhanced response should still contain [PROSPECT]: tags (just with different questions)
      const hasProspectTags = /\[PROSPECT\]:/i.test(enhancedResponse);
      if (!hasProspectTags && /\[PROSPECT\]:/i.test(originalResponse)) {
        this.logError('Enhanced response lost all PROSPECT tags');
        return false;
      }

      return true;
    } catch (error) {
      this.logError('Response validation error', error);
      return false;
    }
  }

  /**
   * Get detailed processing result for debugging
   */
  processWithDetails(response: string): ProcessingResult {
    const startTime = performance.now();
    
    try {
      const matches = this.findProspectTags(response);
      const enhancedResponse = this.enhanceProspectQuestions(response);
      const processingTime = performance.now() - startTime;

      return {
        success: true,
        originalResponse: response,
        enhancedResponse,
        replacementsMade: matches.length,
        processingTimeMs: Math.round(processingTime * 100) / 100
      };
    } catch (error) {
      const processingTime = performance.now() - startTime;
      
      return {
        success: false,
        originalResponse: response,
        enhancedResponse: response, // Fallback to original
        replacementsMade: 0,
        processingTimeMs: Math.round(processingTime * 100) / 100,
        errors: [(error as Error).message]
      };
    }
  }

  /**
   * Test the processor with various input formats
   */
  testProcessor(): Record<string, any> {
    const testCases = [
      // Standard format
      "Great question about BLP benefits.\n\n[PROSPECT]: What happens if I die in another state?",
      
      // Multiple prospect tags
      "BLP provides immediate access.\n\n[PROSPECT]: How quickly can my family get the money?\n\nAdditionally, consider this:\n\n[PROSPECT]: What if funeral costs have increased?",
      
      // No prospect tags
      "This is a standard response without any prospect questions.",
      
      // Malformed tags
      "Response with [PROSPECT What about my situation?\nAnother line here.",
      
      // Edge cases
      "[PROSPECT]: Question at start of response.",
      "Response ending with [PROSPECT]: Final question",
      "",  // Empty response
      
      // Fallback format variations
      "Test [PROSPECT] Missing colon format",
      "Test PROSPECT: Missing brackets format",
      "Test [PROSPECT Missing closing bracket",
      "Test [PROSPECT ]: Extra space format"
    ];

    const results = testCases.map((testCase, index) => {
      const result = this.processWithDetails(testCase);
      return {
        testIndex: index,
        input: testCase.substring(0, 100) + (testCase.length > 100 ? '...' : ''),
        inputLength: testCase.length,
        success: result.success,
        replacementsMade: result.replacementsMade,
        processingTimeMs: result.processingTimeMs,
        outputLength: result.enhancedResponse.length,
        changed: result.originalResponse !== result.enhancedResponse
      };
    });

    return {
      testCases: results,
      summary: {
        totalTests: results.length,
        successful: results.filter(r => r.success).length,
        withReplacements: results.filter(r => r.replacementsMade > 0).length,
        averageProcessingTime: Math.round((results.reduce((sum, r) => sum + r.processingTimeMs, 0) / results.length) * 100) / 100
      }
    };
  }

  /**
   * Update processor configuration
   */
  updateConfig(newConfig: Partial<ProcessorConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): ProcessorConfig {
    return { ...this.config };
  }

  /**
   * Log replacement details for debugging
   */
  private logReplacement(match: ProspectMatch, newQuestion: string, questionId: number): void {
    if (!this.config.logReplacements) return;

    console.log(`[ResponseProcessor] Replaced question ${questionId}: "${match.originalQuestion.substring(0, 50)}..." → "${newQuestion.substring(0, 50)}..." [Format: ${match.tagFormat}]`);
  }

  /**
   * Log errors with context
   */
  private logError(message: string, context?: any): void {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[ResponseProcessor] ${message}:`, context);
    }
  }
}

// Export singleton instance
export const responseProcessor = ResponseProcessor.getInstance();

// Export types
export type { ProcessingResult, ProcessorConfig, ProspectMatch };